/**
 * PropertyGA4 — Google Analytics 4 lens for one property.
 * Real visitor sessions, channels, devices, landing-page bounce.
 */
import { useParams } from "react-router-dom";
import {
  Activity, Globe, Smartphone, MapPin, BarChart3,
  Zap, ShoppingCart, Clock, Cpu, Building,
} from "lucide-react";
import {
  useProperty, useCapabilitySample, useStartGA4Pull,
} from "@/lib/queries";
import { VendorLens, DataTable, Headline } from "@/components/VendorLens";


export default function PropertyGA4() {
  const { propertyId: rawId } = useParams<{ propertyId: string }>();
  const propertyId = Number(rawId);
  const property = useProperty(propertyId);
  const pull = useStartGA4Pull(propertyId);

  if (property.isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!property.data) return <div className="text-sm text-red-600">Property not found.</div>;
  const p = property.data;

  return (
    <VendorLens
      vendorIcon={<Activity className="h-5 w-5 text-primary" />}
      vendorTitle="Google Analytics 4"
      vendorDescription={
        <>
          Real visitor sessions on {p.domain} — channel mix, device split,
          landing-page bounce rates, daily trend. The most authoritative
          traffic data we have.
        </>
      }
      pullCostNote="Free (GA4 Data API). Takes ~3-5s for 8 reports."
      pullMutation={pull}
      tabs={[
        { key: "overview", label: "Overview", body: <OverviewTab propertyId={propertyId} /> },
        { key: "pages",    label: "Top pages",  body: <TopPagesTab propertyId={propertyId} /> },
        { key: "channels", label: "Channels",   body: <ChannelsTab propertyId={propertyId} /> },
        { key: "sources",  label: "Sources",    body: <SourcesTab propertyId={propertyId} /> },
        { key: "landing",  label: "Landing pages", body: <LandingTab propertyId={propertyId} /> },
        { key: "devices",  label: "Devices",    body: <DevicesTab propertyId={propertyId} /> },
        { key: "countries", label: "Countries", body: <CountriesTab propertyId={propertyId} /> },
        { key: "browsers", label: "Browsers",   body: <BrowsersTab propertyId={propertyId} /> },
        { key: "daily",    label: "Daily trend", body: <DailyTab propertyId={propertyId} /> },
        // D.11c — new
        { key: "events",   label: "Events",     body: <EventsTab propertyId={propertyId} /> },
        { key: "conversions", label: "Conversions", body: <ConversionsTab propertyId={propertyId} /> },
        { key: "ecommerce", label: "E-commerce", body: <EcommerceTab propertyId={propertyId} /> },
        { key: "acquisition", label: "Acquisition", body: <AcquisitionTab propertyId={propertyId} /> },
        { key: "hourly",   label: "Hourly",     body: <HourlyTab propertyId={propertyId} /> },
        { key: "os",       label: "OS",         body: <OSTab propertyId={propertyId} /> },
        { key: "cities",   label: "Cities",     body: <CitiesTab propertyId={propertyId} /> },
      ]}
    />
  );
}


// GA4 rows shape: {dimensions: {dimName: val}, metrics: {metName: val (string!)}}
function dim(r: Record<string, unknown>, k: string): string {
  return ((r.dimensions as Record<string, string> | undefined)?.[k]) ?? "";
}
function met(r: Record<string, unknown>, k: string): number {
  const v = (r.metrics as Record<string, string> | undefined)?.[k];
  return v ? Number(v) : 0;
}


function OverviewTab({ propertyId }: { propertyId: number }) {
  const channels = useCapabilitySample("ga4", "run_report_sessionDefaultChannelGroup_by_sessions", { propertyId });
  const daily    = useCapabilitySample("ga4", "run_report_date_by_sessions", { propertyId });
  const pages    = useCapabilitySample("ga4", "run_report_pagePath_by_sessions", { propertyId });

  const chRows = (((channels.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const dRows  = (((daily.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const pRows  = (((pages.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];

  const totalSessions = chRows.reduce((s, r) => s + met(r, "sessions"), 0);
  const totalUsers = chRows.reduce((s, r) => s + met(r, "totalUsers"), 0);
  const organicSessions = chRows
    .filter((r) => dim(r, "sessionDefaultChannelGroup")?.toLowerCase().includes("organic"))
    .reduce((s, r) => s + met(r, "sessions"), 0);
  const organicShare = totalSessions > 0 ? (organicSessions / totalSessions * 100) : 0;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Headline label="Sessions (28d)"      value={totalSessions} />
        <Headline label="Users (28d)"          value={totalUsers} />
        <Headline label="Organic share"        value={organicShare > 0 ? `${organicShare.toFixed(1)}%` : undefined} />
        <Headline label="Tracked pages"        value={pRows.length} />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Headline label="Days tracked (90d window)" value={dRows.length} />
        <Headline label="Channels active"      value={chRows.length} />
      </div>
    </div>
  );
}


function TopPagesTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ga4", "run_report_pagePath_by_sessions", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => met(b, "sessions") - met(a, "sessions"));
  return (
    <DataTable
      title="Top pages by sessions (last 28d)"
      subtitle="Real visitor traffic per page. Sorted by sessions descending."
      sample={sample.data} rowCount={sorted.length}
      icon={<Globe className="h-5 w-5 text-primary" />}
      columns={[
        { key: "page", label: "Page", className: "font-mono text-[11px] max-w-[36%]",
          render: (r) => dim(r, "pagePath") },
        { key: "sessions", label: "Sessions", align: "right",
          render: (r) => met(r, "sessions").toLocaleString() },
        { key: "screenPageViews", label: "Pageviews", align: "right",
          render: (r) => met(r, "screenPageViews").toLocaleString() },
        { key: "engagementRate", label: "Engagement", align: "right",
          render: (r) => `${(met(r, "engagementRate") * 100).toFixed(1)}%` },
        { key: "bounceRate", label: "Bounce", align: "right",
          render: (r) => `${(met(r, "bounceRate") * 100).toFixed(1)}%` },
        { key: "averageSessionDuration", label: "Avg duration", align: "right",
          render: (r) => `${met(r, "averageSessionDuration").toFixed(0)}s` },
      ]}
      rows={sorted}
    />
  );
}


function ChannelsTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ga4", "run_report_sessionDefaultChannelGroup_by_sessions", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => met(b, "sessions") - met(a, "sessions"));
  return (
    <DataTable
      title="Sessions by channel (last 28d)"
      subtitle="Direct vs Organic vs Paid vs Referral vs Social vs Email."
      sample={sample.data} rowCount={sorted.length}
      icon={<BarChart3 className="h-5 w-5 text-primary" />}
      columns={[
        { key: "channel", label: "Channel",
          render: (r) => dim(r, "sessionDefaultChannelGroup") },
        { key: "sessions", label: "Sessions", align: "right",
          render: (r) => met(r, "sessions").toLocaleString() },
        { key: "totalUsers", label: "Users", align: "right",
          render: (r) => met(r, "totalUsers").toLocaleString() },
        { key: "engagedSessions", label: "Engaged", align: "right",
          render: (r) => met(r, "engagedSessions").toLocaleString() },
        { key: "conversions", label: "Conversions", align: "right",
          render: (r) => met(r, "conversions").toLocaleString() },
      ]}
      rows={sorted}
    />
  );
}


function SourcesTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ga4", "run_report_sessionSource-sessionMedium_by_sessions", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => met(b, "sessions") - met(a, "sessions"));
  return (
    <DataTable
      title="Source / Medium breakdown (last 28d)"
      subtitle="Per-source-medium session counts."
      sample={sample.data} rowCount={sorted.length}
      icon={<BarChart3 className="h-5 w-5 text-primary" />}
      columns={[
        { key: "source", label: "Source",
          render: (r) => dim(r, "sessionSource") },
        { key: "medium", label: "Medium",
          render: (r) => dim(r, "sessionMedium") },
        { key: "sessions", label: "Sessions", align: "right",
          render: (r) => met(r, "sessions").toLocaleString() },
        { key: "totalUsers", label: "Users", align: "right",
          render: (r) => met(r, "totalUsers").toLocaleString() },
      ]}
      rows={sorted}
    />
  );
}


function LandingTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ga4", "run_report_landingPage_by_sessions", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => met(b, "sessions") - met(a, "sessions"));
  return (
    <DataTable
      title="Landing pages with bounce + engagement"
      subtitle="High-bounce-on-high-traffic = highest-leverage page-quality fixes."
      sample={sample.data} rowCount={sorted.length}
      icon={<Globe className="h-5 w-5 text-primary" />}
      columns={[
        { key: "page", label: "Landing page", className: "font-mono text-[11px] max-w-[40%]",
          render: (r) => dim(r, "landingPage") },
        { key: "sessions", label: "Sessions", align: "right",
          render: (r) => met(r, "sessions").toLocaleString() },
        { key: "bounceRate", label: "Bounce", align: "right",
          render: (r) => `${(met(r, "bounceRate") * 100).toFixed(1)}%` },
        { key: "engagementRate", label: "Engagement", align: "right",
          render: (r) => `${(met(r, "engagementRate") * 100).toFixed(1)}%` },
      ]}
      rows={sorted}
    />
  );
}


function DevicesTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ga4", "run_report_deviceCategory_by_sessions", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  return (
    <DataTable
      title="Sessions by device" subtitle="Mobile vs desktop vs tablet split."
      sample={sample.data} rowCount={rows.length}
      icon={<Smartphone className="h-5 w-5 text-primary" />}
      columns={[
        { key: "device", label: "Device", render: (r) => dim(r, "deviceCategory") },
        { key: "sessions", label: "Sessions", align: "right",
          render: (r) => met(r, "sessions").toLocaleString() },
        { key: "engagementRate", label: "Engagement", align: "right",
          render: (r) => `${(met(r, "engagementRate") * 100).toFixed(1)}%` },
        { key: "bounceRate", label: "Bounce", align: "right",
          render: (r) => `${(met(r, "bounceRate") * 100).toFixed(1)}%` },
      ]}
      rows={rows}
    />
  );
}


function CountriesTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ga4", "run_report_country_by_sessions", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => met(b, "sessions") - met(a, "sessions"));
  return (
    <DataTable
      title="Sessions by country" subtitle="Geographic distribution of real visitors."
      sample={sample.data} rowCount={sorted.length}
      icon={<MapPin className="h-5 w-5 text-primary" />}
      columns={[
        { key: "country", label: "Country", render: (r) => dim(r, "country") },
        { key: "sessions", label: "Sessions", align: "right",
          render: (r) => met(r, "sessions").toLocaleString() },
      ]}
      rows={sorted}
    />
  );
}


function BrowsersTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ga4", "run_report_browser_by_sessions", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => met(b, "sessions") - met(a, "sessions"));
  return (
    <DataTable
      title="Sessions by browser" subtitle="Browser-share view (helps spot Safari/Firefox-only bugs)."
      sample={sample.data} rowCount={sorted.length}
      columns={[
        { key: "browser", label: "Browser", render: (r) => dim(r, "browser") },
        { key: "sessions", label: "Sessions", align: "right",
          render: (r) => met(r, "sessions").toLocaleString() },
      ]}
      rows={sorted}
    />
  );
}


function DailyTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ga4", "run_report_date_by_sessions", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => dim(a, "date").localeCompare(dim(b, "date")));
  return (
    <DataTable
      title="Daily sessions trend (last 90d)"
      subtitle="Daily visitor + session counts. Spot week-over-week shifts at a glance."
      sample={sample.data} rowCount={sorted.length}
      columns={[
        { key: "date", label: "Date", render: (r) => {
          const d = dim(r, "date");
          if (d.length === 8) return `${d.slice(0,4)}-${d.slice(4,6)}-${d.slice(6,8)}`;
          return d;
        }},
        { key: "sessions", label: "Sessions", align: "right",
          render: (r) => met(r, "sessions").toLocaleString() },
        { key: "totalUsers", label: "Users", align: "right",
          render: (r) => met(r, "totalUsers").toLocaleString() },
        { key: "newUsers", label: "New users", align: "right",
          render: (r) => met(r, "newUsers").toLocaleString() },
      ]}
      rows={sorted}
    />
  );
}


// ─── Sprint D.11c — new tab components ─────────────────────────────


function EventsTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ga4", "run_report_eventName_by_eventCount", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => met(b, "eventCount") - met(a, "eventCount"));
  return (
    <DataTable
      title="Top GA4 events (last 28d)"
      subtitle="All tracked events ranked by count. Sudden 0s = tracking break (cross-reference with the conversion_event_drop finding)."
      sample={sample.data} rowCount={sorted.length}
      icon={<Zap className="h-5 w-5 text-primary" />}
      columns={[
        { key: "eventName", label: "Event", className: "font-mono text-[11px]",
          render: (r) => dim(r, "eventName") },
        { key: "eventCount", label: "Count", align: "right",
          render: (r) => met(r, "eventCount").toLocaleString() },
        { key: "totalUsers", label: "Users", align: "right",
          render: (r) => met(r, "totalUsers").toLocaleString() },
      ]}
      rows={sorted}
    />
  );
}


function ConversionsTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ga4", "run_report_eventName_by_conversions", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => met(b, "conversions") - met(a, "conversions"));
  return (
    <DataTable
      title="Events by conversion count"
      subtitle="Which events are doing the conversion-driving work. Revenue per event surfaces here."
      sample={sample.data} rowCount={sorted.length}
      icon={<BarChart3 className="h-5 w-5 text-primary" />}
      columns={[
        { key: "eventName", label: "Event", className: "font-mono text-[11px]",
          render: (r) => dim(r, "eventName") },
        { key: "conversions", label: "Conversions", align: "right",
          render: (r) => met(r, "conversions").toLocaleString() },
        { key: "purchaseRevenue", label: "Revenue", align: "right",
          render: (r) => `$${met(r, "purchaseRevenue").toLocaleString()}` },
        { key: "totalUsers", label: "Users", align: "right",
          render: (r) => met(r, "totalUsers").toLocaleString() },
      ]}
      rows={sorted}
    />
  );
}


function EcommerceTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ga4", "run_report_itemName_by_itemsViewed", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => met(b, "itemsViewed") - met(a, "itemsViewed"));
  return (
    <DataTable
      title="Top products — funnel (viewed → cart → checkout → purchased)"
      subtitle="Enhanced E-commerce events. Drop-off ratios surface checkout-funnel breaks."
      sample={sample.data} rowCount={sorted.length}
      icon={<ShoppingCart className="h-5 w-5 text-primary" />}
      columns={[
        { key: "itemName", label: "Product", className: "max-w-[34%] truncate",
          render: (r) => dim(r, "itemName") },
        { key: "itemsViewed", label: "Viewed", align: "right",
          render: (r) => met(r, "itemsViewed").toLocaleString() },
        { key: "itemsAddedToCart", label: "→ Cart", align: "right",
          render: (r) => met(r, "itemsAddedToCart").toLocaleString() },
        { key: "itemsCheckedOut", label: "→ Checkout", align: "right",
          render: (r) => met(r, "itemsCheckedOut").toLocaleString() },
        { key: "itemsPurchased", label: "→ Purchased", align: "right",
          render: (r) => met(r, "itemsPurchased").toLocaleString() },
        { key: "itemRevenue", label: "Revenue", align: "right",
          render: (r) => `$${met(r, "itemRevenue").toLocaleString()}` },
      ]}
      rows={sorted}
    />
  );
}


function AcquisitionTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ga4", "run_report_firstUserDefaultChannelGroup_by_newUsers", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => met(b, "newUsers") - met(a, "newUsers"));
  return (
    <DataTable
      title="First-session acquisition by channel"
      subtitle="Where NEW users first found us. Different from regular Channels — that one shows all sessions; this shows first-touch attribution."
      sample={sample.data} rowCount={sorted.length}
      icon={<Globe className="h-5 w-5 text-primary" />}
      columns={[
        { key: "firstUserDefaultChannelGroup", label: "First-touch channel",
          render: (r) => dim(r, "firstUserDefaultChannelGroup") },
        { key: "newUsers", label: "New users", align: "right",
          render: (r) => met(r, "newUsers").toLocaleString() },
        { key: "totalUsers", label: "Total users", align: "right",
          render: (r) => met(r, "totalUsers").toLocaleString() },
        { key: "conversions", label: "Conversions", align: "right",
          render: (r) => met(r, "conversions").toLocaleString() },
        { key: "purchaseRevenue", label: "Revenue", align: "right",
          render: (r) => `$${met(r, "purchaseRevenue").toLocaleString()}` },
      ]}
      rows={sorted}
    />
  );
}


function HourlyTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ga4", "run_report_hour_by_sessions", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => Number(dim(a, "hour")) - Number(dim(b, "hour")));
  return (
    <DataTable
      title="Traffic by hour (last 7 days)"
      subtitle="0-23 UTC. Spikes outside business hours = batch/cron/bot traffic worth investigating."
      sample={sample.data} rowCount={sorted.length}
      icon={<Clock className="h-5 w-5 text-primary" />}
      columns={[
        { key: "hour", label: "Hour", align: "right",
          render: (r) => `${dim(r, "hour")}:00` },
        { key: "sessions", label: "Sessions", align: "right",
          render: (r) => met(r, "sessions").toLocaleString() },
        { key: "totalUsers", label: "Users", align: "right",
          render: (r) => met(r, "totalUsers").toLocaleString() },
      ]}
      rows={sorted}
    />
  );
}


function OSTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ga4", "run_report_operatingSystem_by_sessions", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => met(b, "sessions") - met(a, "sessions"));
  return (
    <DataTable
      title="Operating systems"
      subtitle="Sub-breakdown of mobile/desktop. iOS vs Android engagement gap surfaces here."
      sample={sample.data} rowCount={sorted.length}
      icon={<Cpu className="h-5 w-5 text-primary" />}
      columns={[
        { key: "operatingSystem", label: "OS",
          render: (r) => dim(r, "operatingSystem") },
        { key: "sessions", label: "Sessions", align: "right",
          render: (r) => met(r, "sessions").toLocaleString() },
        { key: "engagementRate", label: "Engagement rate", align: "right",
          render: (r) => `${(met(r, "engagementRate") * 100).toFixed(1)}%` },
      ]}
      rows={sorted}
    />
  );
}


function CitiesTab({ propertyId }: { propertyId: number }) {
  const sample = useCapabilitySample("ga4", "run_report_city_by_sessions", { propertyId });
  const rows = (((sample.data?.response as Record<string, unknown> | undefined)?.rows) as Array<Record<string, unknown>> | undefined) ?? [];
  const sorted = [...rows].sort((a, b) => met(b, "sessions") - met(a, "sessions"));
  return (
    <DataTable
      title="Top cities"
      subtitle="Geographic depth below country. Useful for local-business properties + ad targeting validation."
      sample={sample.data} rowCount={sorted.length}
      icon={<Building className="h-5 w-5 text-primary" />}
      columns={[
        { key: "city", label: "City",
          render: (r) => dim(r, "city") },
        { key: "sessions", label: "Sessions", align: "right",
          render: (r) => met(r, "sessions").toLocaleString() },
        { key: "totalUsers", label: "Users", align: "right",
          render: (r) => met(r, "totalUsers").toLocaleString() },
        { key: "engagementRate", label: "Engagement rate", align: "right",
          render: (r) => `${(met(r, "engagementRate") * 100).toFixed(1)}%` },
      ]}
      rows={sorted}
    />
  );
}
